import RateDisplay from "@components/Cards/RateDisplay";
import { useFormatContext } from "@lib/hooks/format-context";
import { CellContext, HeaderContext } from "@tanstack/react-table";
import React from "react";
import { getCorrelation, getTotalValueOfAllRows } from "./calculations";
import { MetricInsightIcon } from "@components/MetricInsightIcon/MetricInsightIcon";
import { AdAccountChannels } from "@lib/api-hooks/useAdAccounts/types";

type Props<T> = {
  compared?: boolean;
  highlightMinMaxValues?: boolean;
  channel?: AdAccountChannels;
} & (
  | {
      info: CellContext<T, number | undefined>;
      calculateTotals?: false;
      total?: never;
      comparedTotal?: never;
    }
  | {
      info: HeaderContext<T, number | undefined>;
      calculateTotals: true;
      total?: never;
      comparedTotal?: never;
    }
  | {
      total: number;
      comparedTotal?: number;
      info?: never;
      calculateTotals?: never;
    }
);

export type CurrencyCalculatorType = (
  value: number,
  numDigits?: number
) => string;

const getDisplayString = (
  value: number,
  formatNumber: CurrencyCalculatorType
) => {
  return formatNumber(value, 2);
};

function RoasTableDisplay<T extends Record<string, any>>({
  info,
  calculateTotals,
  compared,
  highlightMinMaxValues,
  total,
  comparedTotal,
  channel,
}: Props<T>) {
  const { formatNumber } = useFormatContext();
  let comparedValue = 0;
  let value = 0;
  let spend = 0;
  let aov = 0;

  if (total !== undefined) {
    value = total;
    comparedValue = comparedTotal ?? 0;
  } else if (calculateTotals && info) {
    const allSpendValues = info.column.getFacetedRowModel().rows.map((row) => {
      return {
        value: row.original?.["spend"],
        compared: row.original?.compared?.["spend"],
      };
    });
    const allRevenueValues = info.column
      .getFacetedRowModel()
      .rows.map((row) => {
        return {
          value: row.original?.["purchaseAmount"],
          compared: row.original?.compared?.["purchaseAmount"],
        };
      });
    const spendTotals = getTotalValueOfAllRows({
      values: allSpendValues,
    });
    const revenueTotals = getTotalValueOfAllRows({
      values: allRevenueValues,
    });

    value =
      revenueTotals.value && spendTotals.value
        ? revenueTotals.value / spendTotals.value
        : 0;
    comparedValue =
      revenueTotals.compared && spendTotals.compared
        ? revenueTotals.compared / spendTotals.compared
        : 0;
    spend = spendTotals.value || 0;
  } else {
    value = info.getValue() ?? 0;
    if (compared && info.row.original?.compared) {
      comparedValue = info.row.original?.compared[info.column?.id];
    }
    spend = info.row.original?.spend || 0;
    aov = info.row.original?.aov || 0;
  }

  const rowHasValue = value || comparedValue;
  if (!rowHasValue) return "-";

  let valueClassNames = "";
  if (highlightMinMaxValues && info) {
    const values = info.column.getFacetedMinMaxValues();
    if (
      values &&
      ((Array.isArray(values[0]) && values[0][0] === value) ||
        values[0] === value)
    ) {
      valueClassNames =
        "bg-green-500/20 dark:bg-green-600/20 text-green-800 dark:text-green-200 py-0.5 px-1.5 rounded-lg";
    } else if (
      values &&
      ((Array.isArray(values[1]) && values[1][0] === value) ||
        values[1] === value)
    ) {
      valueClassNames =
        "bg-green-500 dark:bg-green-600 text-green-100 py-0.5 px-1.5 rounded-lg";
    }
  }

  const shouldShowInsight = !calculateTotals;

  if (compared) {
    const rate = getCorrelation(value, comparedValue, 2);
    return (
      <div className="flex items-center w-full gap-2">
        <div className="flex-1 flex items-start flex-col">
          <div className="flex items-center">
            <p className={value > 0 ? valueClassNames : ""}>
              {getDisplayString(value, formatNumber)}
            </p>
            {shouldShowInsight && (
              <MetricInsightIcon
                metricKey="return_on_ad_spend"
                channel={channel}
                value={value}
                significanceValues={{
                  spend: spend,
                  aov: aov,
                }}
              />
            )}
          </div>
          <p className="text-[11px] leading-tight opacity-70">
            vs {getDisplayString(comparedValue, formatNumber)}
          </p>
        </div>
        <RateDisplay rate={rate} isIncreasing={rate > 0} size="small" />
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <span>{getDisplayString(value, formatNumber)}</span>
      {shouldShowInsight && (
        <MetricInsightIcon
          metricKey="return_on_ad_spend"
          channel={channel}
          value={value}
          significanceValues={{
            spend: spend,
            aov: aov,
          }}
        />
      )}
    </div>
  );
}

export default RoasTableDisplay;
